/**
 * Client-mintable ULID generation.
 *
 * Every contract node carries a ULID `id` (see contract/schema — the `ulid`
 * $def, pattern `^[0-9A-HJKMNP-TV-Z]{26}$`). Clients and QueVee mint ids
 * offline and reference them immediately (e.g. addPane → addPlot into it in
 * one batch), so id generation must be local, dependency-free, and produce
 * time-ordered, index-friendly values.
 *
 * A ULID is 26 Crockford-Base32 chars: 48-bit millisecond timestamp (10 chars)
 * + 80 bits of randomness (16 chars). This factory is monotonic — ids minted
 * within the same millisecond increment the random component, so a batch is
 * always strictly sortable in creation order.
 */

// Crockford Base32 — excludes I, L, O, U to avoid ambiguity.
const ENCODING = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const ENCODING_LEN = ENCODING.length; // 32
const TIME_LEN = 10;
const RANDOM_LEN = 16;
const MAX_TIME = 0xffffffffffff; // 2^48 - 1

/** Matches the contract's `ulid` $def pattern. */
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/** True if `value` is a syntactically valid ULID string. */
export function isUlid(value: unknown): value is string {
  return typeof value === "string" && ULID_RE.test(value);
}

function encodeTime(time: number): string {
  if (!Number.isInteger(time) || time < 0 || time > MAX_TIME) {
    throw new RangeError(`ULID timestamp out of range: ${time}`);
  }
  let remaining = time;
  let out = "";
  for (let i = 0; i < TIME_LEN; i++) {
    out = ENCODING.charAt(remaining % ENCODING_LEN) + out;
    remaining = Math.floor(remaining / ENCODING_LEN);
  }
  return out;
}

/** 16 independent Base32 symbols (0..31), 80 bits of randomness. */
function randomSymbols(): number[] {
  const bytes = new Uint8Array(RANDOM_LEN);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b % ENCODING_LEN);
}

/** Increment a Base32 symbol array (least-significant last), for monotonicity. */
function incrementSymbols(symbols: number[]): number[] {
  const out = symbols.slice();
  for (let i = out.length - 1; i >= 0; i--) {
    const v = out[i] ?? 0;
    if (v < ENCODING_LEN - 1) {
      out[i] = v + 1;
      return out;
    }
    out[i] = 0;
  }
  // Overflowed all 80 random bits within one ms — astronomically unlikely.
  // Reseed rather than wrap to a smaller value (which would break ordering).
  return randomSymbols();
}

/**
 * Create a monotonic ULID factory. Prefer the shared {@link mintUlid} unless
 * you need an isolated sequence (e.g. deterministic tests via a custom clock).
 *
 * @param now  Clock source in epoch-ms. Defaults to `Date.now`.
 */
export function createUlidFactory(now: () => number = Date.now): () => string {
  let lastTime = -1;
  let lastRandom: number[] = [];
  return function mint(): string {
    const time = now();
    if (time === lastTime) {
      lastRandom = incrementSymbols(lastRandom);
    } else {
      lastTime = time;
      lastRandom = randomSymbols();
    }
    let random = "";
    for (const sym of lastRandom) random += ENCODING.charAt(sym);
    return encodeTime(time) + random;
  };
}

/** Mint a new monotonic ULID from the process-wide sequence. */
export const mintUlid: () => string = createUlidFactory();